import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { createPortal } from 'react-dom';

import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';

/**
 * Attach the child node in a view zone at the end of the given line.
 */
export const EditorViewZone = ({
  editor,
  lineNumber,
  children,
  className,
  viewZoneOptions,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  lineNumber: number;
  children: React.ReactNode;
  className: string;
  viewZoneOptions?: Omit<
    monacoEditor.editor.IViewZone,
    'afterLineNumber' | 'domNode'
  >;
}) => {
  const domNode = React.useMemo(() => document.createElement('div'), []);

  const currentViewZone = React.useRef<{
    zoneId: string;
    zone: monacoEditor.editor.IViewZone;
  }>();

  // View zones do not work properly when attached to an editor before it is
  // mounted. This delayedEditor will not be populated until the first render
  // cycle has completed.
  const [delayedEditor, setDelayedEditor] =
    React.useState<monacoEditor.editor.IStandaloneCodeEditor>();

  const model = useEditorModel(editor);
  React.useEffect(() => {
    delayedEditor?.changeViewZones((changeAccessor) => {
      const afterLineNumber = lineNumber - 1;
      if (!currentViewZone.current) {
        domNode.className = className;
        const newZone = {
          afterLineNumber,
          heightInLines: 0,
          domNode,
          ...viewZoneOptions,
        };
        currentViewZone.current = {
          zone: newZone,
          zoneId: changeAccessor.addZone(newZone),
        };
      } else if (
        currentViewZone.current.zone.afterLineNumber !== afterLineNumber
      ) {
        currentViewZone.current.zone.afterLineNumber = afterLineNumber;
        changeAccessor.layoutZone(currentViewZone.current.zoneId);
      }
    });
    // when models change, view zones are lost so it needs to be in the dependency array
  }, [lineNumber, domNode, delayedEditor, model, className, viewZoneOptions]);

  React.useEffect(() => {
    // Remove viewZone when component unmounts or editor changes instance
    return () => {
      const lastZone = currentViewZone.current;
      if (!lastZone) return;
      editor.changeViewZones((changeAccessor) => {
        changeAccessor.removeZone(lastZone.zoneId);
      });
      currentViewZone.current = undefined;
    };
  }, [editor]);

  React.useEffect(() => {
    setDelayedEditor(editor);
  }, [editor]);

  return createPortal(children, domNode);
};
